import React, { createContext, useContext, useEffect, useState } from "react"

/*
   These objects are kept in sync with the CSS variables defined in
   `styles/global.css` ( :root for light, [data-theme="dark"] for dark ).
   Components that only need CSS (className/style with var(--token)) don't
   need to touch this file at all. Components that compute colors in JS
   (canvas/SVG fills, chart.js options, etc.) should read from `useTheme()`
   so they re-render correctly when the mode changes.
*/

export const themes = {
  light: {
    mode: "light",

    primary: "#FF6B7A",
    primaryHover: "#FF8A96",
    secondary: "#FF8A5C",

    background: "#FFF8F8",
    cardBackground: "#FFFFFF",

    textPrimary: "#1A2233",
    textSecondary: "#55617A",
    textMuted: "#94A0B8",

    border: "#E2E8F0",
    hover: "#F1F5FB",
    shadow: "rgba(15, 23, 42, 0.08)",
    surfaceDark: "#172033",

    country: "#F1E8EA",
    countryBorder: "#FFFFFF",

    availableCountry: "#35B978",
    unavailableCountry: "#E5DADD",

    tooltipBackground: "#1A2233",
    tooltipText: "#FFFFFF",

    chartSingleMode: "#FF6B7A",
    chartComparisonMode: "#D63DE4",
    // Bar/area fill opacity suffix (hex alpha) appended to a solid color (lower in light mode)
    chartFillAlpha: "4D", // ~30%

    // Categorical palette for world regions. Keyed by the `code`.
    regions: {
      SSA: "#FF6B7A",
      ASIA: "#E95768",
      LATAM: "#FF806F",
      MENA: "#C94F62",
    },


    // Categorical palette for indicator groups.
    groups: {
      eco: "#35B978",
      energy: "#3D77E4",
      cmdty: "#E4A43D",
      trade: "#D63DE4",
    },
  },
  dark: {
    mode: "dark",

    primary: "#FF6B7A",
    primaryHover: "#FF8A96",
    secondary: "#FF8A5C",

    background: "#15191F",
    cardBackground: "#1D2229",

    textPrimary: "#E9EDF2",
    textSecondary: "#B5BEC9",
    textMuted: "#7E8996",

    border: "#303841",
    hover: "#262C34",
    shadow: "rgba(0, 0, 0, 0.30)",
    surfaceDark: "#242A32",

    country: "#342F33",
    countryBorder: "#15191F",

    availableCountry: "#35B978",
    unavailableCountry: "#4A3B40",

    tooltipBackground: "#303841",
    tooltipText: "#F2F5F8",

    chartSingleMode: "#FF6B7A",
    chartComparisonMode: "#D63DE4",
    chartFillAlpha: "66", // ~40%

    // Same region codes as light mode, but lighter/more saturated so they
    // stay legible against the dark navy background.
    regions: {
      SSA: "#FF7180",
      ASIA: "#E85B6D",
      LATAM: "#FF8875",
      MENA: "#D15769",
    },

    groups: {
      eco: "#3FA875",
      energy: "#5A83CC",
      cmdty: "#C8964A",
      trade: "#A864C7",
    },

  },
}

// callback if color is not provided
export const resolveCategoryColor = (paletteMap, code, apiColor) => {
  if (code && paletteMap && paletteMap[code]) return paletteMap[code]
  return apiColor || "#94A0B8"
}

const STORAGE_KEY = "app-theme-mode"

const getInitialMode = () => {
  if (typeof window === "undefined") return "light"
  const saved = window.localStorage.getItem(STORAGE_KEY)
  if (saved === "light" || saved === "dark") return saved
  return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light"
}

const ThemeContext = createContext({
  mode: "light",
  colors: themes.light,
  toggleTheme: () => {},
  setMode: () => {},
})

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode)

  useEffect(() => {
    const root = document.documentElement

    root.setAttribute("data-theme", mode)

    root.setAttribute("data-bs-theme", mode)
    root.setAttribute("data-coreui-theme", mode)
    window.localStorage.setItem(STORAGE_KEY, mode)
  }, [mode])

  const toggleTheme = () => setMode((prev) => (prev === "light" ? "dark" : "light"))

  const value = {
    mode,
    colors: themes[mode],
    toggleTheme,
    setMode,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useTheme = () => useContext(ThemeContext)

export default themes.light
